/**
 * src/app/app-routing.module.ts
 *
 * @author Bryan Henry <bryan@payfully.co>
 * @date 4/27/2021
 * @copyright Payfully, 2021
 */

import { inject, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';

import { advanceAccessGuard } from './modules/advance/advance-access.guard';
import { redirectLegacyDeal } from './modules/advance/legacy-deal-redirect';
import { invoicesAccessGuard } from './modules/invoices/invoices-access.guard';
import { accountOnboardingGuard } from './modules/wallet/account-onboarding.guard';
import {
  accountlessActivityGuard,
  advanceHomeGuard,
  advancePreviewGuard,
  moveMoneyAccessGuard,
  walletFeatureGuard,
} from './modules/wallet/advance-only.guard';
import { autoSplitAccessGuard } from './modules/wallet/auto-split-access.guard';
import { increaseAccessGuard } from './modules/wallet/increase-access.guard';

const routes: Routes = [
  {
    path: 'account-setup',
    loadComponent: () =>
      import('./modules/wallet/components/account-onboarding/account-onboarding-page.component').then(
        (m) => m.AccountOnboardingPageComponent,
      ),
  },
  {
    path: 'signup-old',
    loadChildren: () =>
      import('./modules/signup/signup.module').then((m) => m.SignupModule),
  },
  {
    path: 'signup-v1-verify-phone',
    loadChildren: () =>
      import('./modules/signupFixV1/signup-fix-v1.module').then(
        (m) => m.SignupFixV1Module,
      ),
  },
  {
    path: 'signup',
    loadChildren: () =>
      import('./modules/signupV2/signup-v2.module').then(
        (m) => m.SignupV2Module,
      ),
  },
  { path: 'onboarding', redirectTo: 'account-setup' },
  {
    path: 'invite-signup',
    loadChildren: () =>
      import('./modules/invite-signup-new/signup.module').then(
        (m) => m.InviteSignupModule,
      ),
  },
  {
    path: 'invite-signup-old',
    loadComponent: () =>
      import('./pages/invite-signup/invite-signup.component').then(
        (m) => m.InviteSignupComponent,
      ),
  },
  {
    path: 'invite-agent',
    loadChildren: () =>
      import('./modules/invite-agent/invite-agent.module').then(
        (m) => m.InviteAgentModule,
      ),
  },
  {
    path: 'request-to-join-brokerage',
    loadChildren: () =>
      import('./modules/request-to-join-partner/request-to-join-partner.module').then(
        (m) => m.RequestToJoinPartnerModule,
      ),
  },
  {
    path: 're-invitation/:code',
    loadComponent: () =>
      import('./pages/re-invitation/re-invitation.component').then(
        (m) => m.ReInvitationComponent,
      ),
  },
  {
    path: 'astra/finished',
    loadComponent: () =>
      import('./modules/astra/components/astra-finished-event/astra-finished-event.component').then(
        (m) => m.AstraFinishedEventComponent,
      ),
  },
  {
    path: 'tax-documents',
    loadComponent: () =>
      import('./pages/dashboard/pages/tax-documents/tax-documents.component').then(
        (m) => m.TaxDocumentsComponent,
      ),
  },
  {
    path: 'dashboard/tax-documents',
    redirectTo: 'tax-documents',
  },
  {
    path: 'prequalify',
    loadChildren: () =>
      import('./pages/prequalify/prequalify.module').then(
        (m) => m.PrequalifyModule,
      ),
  },
  { path: 'dashboard', redirectTo: 'wallet', pathMatch: 'full' },
  {
    path: 'confirm-deal/:id',
    loadChildren: () =>
      import('./modules/confirm-deal/confirm-deal.module').then(
        (m) => m.ConfirmDealModule,
      ),
  },
  {
    path: 'apply',
    loadChildren: () =>
      import('./modules/advance-application/advance-application.module').then(
        (m) => m.AdvanceApplicationModule,
      ),
  },
  {
    path: 'cloc',
    loadChildren: () =>
      import('./modules/cloc/cloc.module').then((m) => m.ClocModule),
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./modules/profile/components/settings/settings.component').then(
        (m) => m.SettingsComponent,
      ),
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./modules/profile/profile.module').then((m) => m.ProfileModule),
  },
  {
    path: 'invoices',
    canActivate: [invoicesAccessGuard],
    loadChildren: () =>
      import('./modules/invoices/invoices.module').then(
        (m) => m.InvoicesModule,
      ),
  },
  {
    path: 'activity',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-activity/dashboard-activity.component').then(
        (m) => m.DashboardActivityComponent,
      ),
  },
  {
    path: 'dashboard/activity',
    redirectTo: 'activity',
  },
  {
    path: 'bank-accounts/:bankAccountId/settings',
    redirectTo: ({ params, queryParams }) =>
      inject(Router).createUrlTree(['/bank-accounts'], {
        queryParams: { ...queryParams, account: params['bankAccountId'] },
      }),
  },
  {
    path: 'bank-accounts',
    loadComponent: () =>
      import('./modules/wallet/components/linked-accounts/linked-accounts.component').then(
        (m) => m.LinkedAccountsComponent,
      ),
  },
  {
    path: 'dashboard/bank-accounts',
    redirectTo: 'bank-accounts',
  },
  {
    path: 'deal',
    pathMatch: 'full',
    redirectTo: redirectLegacyDeal,
  },
  {
    path: 'dashboard/deal',
    pathMatch: 'full',
    redirectTo: redirectLegacyDeal,
  },
  {
    path: 'sign-user-agreement',
    loadComponent: () =>
      import('./pages/dashboard/pages/sign-user-agreement/sign-user-agreement.component').then(
        (m) => m.SignUserAgreementComponent,
      ),
  },
  {
    path: 'dashboard/sign-user-agreement',
    redirectTo: 'sign-user-agreement',
  },
  {
    path: 'new-bank-account',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-new-bank-account/dashboard-new-bank-account.component').then(
        (m) => m.DashboardNewBankAccountComponent,
      ),
  },
  {
    path: 'dashboard/new-bank-account',
    redirectTo: 'new-bank-account',
  },
  {
    path: 'broker',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-broker/dashboard-broker.component').then(
        (m) => m.DashboardBrokerComponent,
      ),
  },
  {
    path: 'dashboard/broker',
    redirectTo: 'broker',
  },
  {
    path: 'payroll-settings',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-payroll-settings/dashboard-payroll-settings.component').then(
        (m) => m.DashboardPayrollSettingsComponent,
      ),
  },
  {
    path: 'dashboard/payroll-settings',
    redirectTo: 'payroll-settings',
  },
  {
    path: 'commissions',
    data: {
      type: 'Commissions',
    },
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-deals/dashboard-deals.component').then(
        (m) => m.DashboardDealsComponent,
      ),
  },
  {
    path: 'dashboard/commissions',
    redirectTo: 'commissions',
  },
  {
    path: 'commission-payment/:id',
    canActivate: [autoSplitAccessGuard],
    loadComponent: () =>
      import('./modules/wallet/components/commission-payment-detail/commission-payment-detail.component').then(
        (m) => m.CommissionPaymentDetailComponent,
      ),
  },
  {
    path: 'commission',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-commission-details/dashboard-commission-details.component').then(
        (m) => m.DashboardCommissionDetailsComponent,
      ),
  },
  {
    path: 'dashboard/commission',
    redirectTo: 'commission',
  },
  {
    path: 'statements',
    loadComponent: () =>
      import('./modules/wallet/components/statements/statements.component').then(
        (m) => m.StatementsComponent,
      ),
  },
  {
    path: 'reporting',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-reporting/dashboard-reporting.component').then(
        (m) => m.DashboardReportingComponent,
      ),
  },
  {
    path: 'dashboard/statements',
    redirectTo: 'statements',
  },
  {
    path: 'advance-only',
    canActivate: [advanceAccessGuard, advancePreviewGuard],
    loadComponent: () =>
      import('./modules/wallet/components/account-preview/advance-only-page.component').then(
        (m) => m.AdvanceOnlyPageComponent,
      ),
  },
  {
    path: 'account-preview/:feature',
    canActivate: [advancePreviewGuard],
    loadComponent: () =>
      import('./modules/wallet/components/account-preview/account-preview.component').then(
        (m) => m.AccountPreviewComponent,
      ),
  },
  {
    path: 'move-money',
    canActivate: [moveMoneyAccessGuard, walletFeatureGuard],
    loadComponent: () =>
      import('./modules/wallet/components/move-money/move-money.component').then(
        (m) => m.MoveMoneyComponent,
      ),
  },
  {
    path: 'wallet',
    canActivate: [advanceHomeGuard],
    loadComponent: () =>
      import('./modules/wallet/components/wallet-home/wallet-home.component').then(
        (m) => m.WalletHomeComponent,
      ),
  },
  {
    path: 'cards',
    canActivate: [walletFeatureGuard],
    loadComponent: () =>
      import('./modules/cards/components/cards/cards.component').then(
        (m) => m.CardsComponent,
      ),
  },
  {
    path: 'advance/request',
    canActivate: [advanceAccessGuard],
    loadChildren: () =>
      import('./modules/cloc/cloc.module').then((m) => m.ClocModule),
  },
  {
    path: 'advance/:dealId/transaction/:kind/:id',
    canActivate: [advanceAccessGuard, advanceHomeGuard],
    loadComponent: () =>
      import('./modules/advance/components/advance-transaction/advance-transaction.component').then(
        (m) => m.AdvanceTransactionComponent,
      ),
  },
  {
    path: 'advance',
    canActivate: [advanceAccessGuard, advanceHomeGuard],
    loadComponent: () =>
      import('./modules/advance/components/advance/advance.component').then(
        (m) => m.AdvanceComponent,
      ),
  },
  {
    path: 'auto-split',
    canActivate: [autoSplitAccessGuard],
    loadComponent: () =>
      import('./modules/wallet/components/auto-split-page/auto-split-page.component').then(
        (m) => m.AutoSplitPageComponent,
      ),
  },
  {
    path: 'account-activity',
    canActivate: [accountlessActivityGuard],
    loadComponent: () =>
      import('./modules/wallet/components/accountless-home/accountless-activity-page.component').then(
        (m) => m.AccountlessActivityPageComponent,
      ),
  },
  {
    path: 'wallet-activity',
    canActivate: [increaseAccessGuard],
    loadComponent: () =>
      import('./modules/wallet/components/wallet-activity-page/wallet-activity-page.component').then(
        (m) => m.WalletActivityPageComponent,
      ),
  },
  {
    path: 'pockets',
    canActivate: [walletFeatureGuard],
    loadComponent: () =>
      import('./modules/pockets/components/pockets/pockets.component').then(
        (m) => m.PocketsComponent,
      ),
  },
  {
    path: 'taxes',
    loadChildren: () =>
      import('./pages/dashboard/pages/taxes/taxes.module').then(
        (m) => m.TaxesModule,
      ),
  },
  {
    path: 'banking',
    loadChildren: () =>
      import('./pages/dashboard/pages/banking/banking.module').then(
        (m) => m.BankingModule,
      ),
  },
  {
    path: 'pdf-viewer',
    loadComponent: () =>
      import('./components/pdf-viewer/pdf-viewer.component').then(
        (m) => m.PdfViewerComponent,
      ),
  },
];

// Public registration and provider callbacks must remain reachable during setup.
const setupProtectedPaths = new Set([
  'advance-only',
  'account-preview/:feature',
  'dashboard',
  'wallet',
  'advance',
  'advance/request',
  'advance/:dealId/transaction/:kind/:id',
  'apply',
  'cloc',
  'move-money',
  'cards',
  'pockets',
  'auto-split',
  'wallet-activity',
  'account-activity',
  'settings',
  'activity',
  'bank-accounts',
  'bank-accounts/:bankAccountId/settings',
  'payroll-settings',
  'profile',
  'invoices',
  'deal',
  'new-bank-account',
  'broker',
  'commissions',
  'commission',
  'commission-payment/:id',
  'statements',
  'reporting',
  'taxes',
  'banking',
  'tax-documents',
  'confirm-deal/:id',
]);
for (const route of routes) {
  if (setupProtectedPaths.has(route.path || '') && !route.redirectTo) {
    route.canActivate = [accountOnboardingGuard, ...(route.canActivate || [])];
    route.canActivateChild = [accountOnboardingGuard];
  }
}

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
